/* @moldura padrao */
/**
 * minhas.tsx (despesas) — "O que eu lancei". Tema AdminUX (Bootstrap).
 *
 * POR QUE EXISTE. A lista de /despesas é da gestão (o backend recusa com 403 os
 * demais). Quem lança e não é gestão — o instalador em campo, a sócia em viagem
 * — não via mais nada depois de salvar. E o que não se vê se lança duas vezes:
 * na dúvida de "será que mandei aquele pedágio?", manda de novo.
 *
 * É a lista das SUAS despesas e nada mais: o backend fixa o usuário pela sessão
 * (GET /despesas/me), não por parâmetro. Só leitura — editar continua sendo
 * da gestão, e por isso a linha não é clicável.
 *
 * Desenhada para o celular: quem usa esta tela está na rua.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '@/lib/api';
import { formatarData } from '@/lib/data';
import { CustomCampoData } from '@/components/CustomCamposData';
import type { DespesaSaida } from './shared';
import { badgeFormaPgto, formatarValor, rotuloFormaPgto } from './shared';
import { CustomMolduraPagina } from '@/components/CustomMolduraPagina';

export function MinhasDespesasPage() {
  const navigate = useNavigate();
  const [lista, setLista] = useState<DespesaSaida[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');

  useEffect(() => {
    let vivo = true;
    setCarregando(true);
    setErro(null);
    const params = new URLSearchParams();
    if (de) params.set('de', de);
    if (ate) params.set('ate', ate);
    const qs = params.toString();
    api
      .get<DespesaSaida[]>(`/despesas/me${qs ? `?${qs}` : ''}`)
      .then((l) => {
        if (vivo) setLista(l ?? []);
      })
      .catch((err) => {
        if (vivo)
          setErro(
            err instanceof ApiError
              ? err.message
              : 'Não foi possível carregar suas despesas.',
          );
      })
      .finally(() => {
        if (vivo) setCarregando(false);
      });
    return () => {
      vivo = false;
    };
  }, [de, ate]);

  // EXCEÇÃO à doc 07, mesmo caso da lista geral: único total, sobre a lista
  // inteira. Morre quando entrar paginação — aí vem do backend, em Decimal.
  const total = useMemo(
    () => lista.reduce((s, d) => s + Number(d.valor ?? 0), 0),
    [lista],
  );

  // Pendente de restituição: o que ela pagou do próprio bolso e ainda não voltou.
  // É o número que quem lançou mais quer ver desta tela.
  const aReceber = useMemo(
    () =>
      lista
        .filter((d) => d.restituicao === 'pendente')
        .reduce((s, d) => s + Number(d.valor ?? 0), 0),
    [lista],
  );

  return (
    <CustomMolduraPagina
      titulo="Minhas despesas"
      descricao="O que você lançou"
      acoes={
        <button className="btn btn-theme" onClick={() => navigate('/despesas/nova')}>
          <i className="bi bi-plus-lg me-1"></i> Nova despesa
        </button>
      }
    >

      <div className="row g-3 mb-3">
        <div className="col-6 col-sm-4 col-lg-3">
          <CustomCampoData valor={de} onChange={setDe} label="De" id="minhas-de" />
        </div>
        <div className="col-6 col-sm-4 col-lg-3">
          <CustomCampoData valor={ate} onChange={setAte} label="Até" id="minhas-ate" />
        </div>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-6">
          <div className="card adminuiux-card h-100">
            <div className="card-body py-3">
              <p className="text-secondary small mb-1">Total lançado</p>
              <h5 className="mb-0">{formatarValor(total)}</h5>
            </div>
          </div>
        </div>
        <div className="col-6">
          <div className="card adminuiux-card h-100">
            <div className="card-body py-3">
              <p className="text-secondary small mb-1">A receber de volta</p>
              <h5 className={`mb-0 ${aReceber > 0 ? 'text-warning' : ''}`}>
                {formatarValor(aReceber)}
              </h5>
            </div>
          </div>
        </div>
      </div>

      {erro && <div className="alert alert-danger">{erro}</div>}

      {carregando ? (
        <div className="d-flex align-items-center justify-content-center gap-2 py-5 text-secondary">
          <span className="spinner-border spinner-border-sm" /> Carregando…
        </div>
      ) : lista.length === 0 ? (
        <div className="card adminuiux-card">
          <div className="card-body text-center text-secondary py-5">
            <i className="bi bi-receipt fs-2 d-block mb-2"></i>
            Você ainda não lançou nenhuma despesa
            {de || ate ? ' neste período' : ''}.
          </div>
        </div>
      ) : (
        <div className="card adminuiux-card">
          <div className="list-group list-group-flush">
            {lista.map((d) => (
              <div key={d.id} className="list-group-item">
                <div className="d-flex justify-content-between align-items-start gap-2">
                  <div className="min-w-0">
                    {/* O HISTÓRICO é o que se lê primeiro: "Pedágio - Rio" diz
                        o que foi melhor que o nome da categoria sozinho. Sem
                        histórico, a categoria assume o lugar. */}
                    <p className="mb-1 fw-medium">
                      {d.historico || d.categoria_nome || 'Sem categoria'}
                    </p>
                    <p className="text-secondary small mb-1">
                      {formatarData(d.data)}
                      {d.historico && d.categoria_nome
                        ? ` • ${d.categoria_nome}`
                        : ''}
                      {d.viagem_label ? ` • ${d.viagem_label}` : ''}
                      {d.os_codigo ? ` • ${d.os_codigo}` : ''}
                    </p>
                    {d.descricao && (
                      <p className="text-secondary small mb-1">{d.descricao}</p>
                    )}
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <span className={`badge ${badgeFormaPgto(d.forma_pgto)}`}>
                        {rotuloFormaPgto(d.forma_pgto)}
                      </span>
                      {d.restituicao === 'pendente' && (
                        <span className="badge text-bg-warning">
                          Restituição pendente
                        </span>
                      )}
                      {d.restituicao === 'restituido' && (
                        <span className="badge text-bg-success">
                          <i className="bi bi-check2 me-1"></i>Restituída
                        </span>
                      )}
                      {d.comprovante && (
                        <a
                          href={d.comprovante}
                          target="_blank"
                          rel="noreferrer"
                          className="small text-decoration-none"
                        >
                          <i className="bi bi-paperclip me-1"></i>comprovante
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="text-end flex-shrink-0">
                    <span className="fw-semibold">{formatarValor(d.valor)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </CustomMolduraPagina>
  );
}
